<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useRequest } from '@/composables/useRequest';
import PurchaseCard from '@/components/cards/PurchaseCard.vue';
const props = defineProps({
  tabs: {
    type: Array,
    default: () => [],
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const computedTabs = computed(() => [
  { id: 'all', title: t('purchases.all_years') },
  ...props.tabs,
]);

const activeTab = ref(route.query.year || 'all');

const perPage = ref(12);
const getInitialFirst = () => {
  const pageQuery = parseInt(route.query.page);
  return pageQuery > 1 ? (pageQuery - 1) * perPage.value : 0;
};

const first = ref(getInitialFirst());
const currentPage = computed(() => Math.floor(first.value / perPage.value) + 1);
const buildRequestParams = () => {
  const params = {};

  if (currentPage.value > 1) params.page = currentPage.value;
  if (activeTab.value !== 'all') params.year = activeTab.value;

  return params;
};

const { data: response, pending } = await useAsyncData(
  'purchases-list',
  () =>
    useRequest('purchases', {
      params: buildRequestParams(),
    }),
  {
    watch: [currentPage, activeTab],
  },
);

const results = computed(() => response.value?.items || []);
const totalRecords = computed(() => response.value?.count || 0);
watch(
  () => response.value?.pagination?.per_page,
  (newPerPage) => {
    if (newPerPage) {
      const current = currentPage.value;
      perPage.value = newPerPage;
      first.value = (current - 1) * newPerPage;
    }
  },
  { immediate: true },
);
watch([currentPage, activeTab], ([page, year]) => {
  const query = { ...route.query };

  if (page > 1) query.page = page.toString();
  else delete query.page;

  if (year !== 'all') query.year = year;
  else delete query.year;

  router.push({ query });
});
const changeTab = (id) => {
  activeTab.value = id;
  first.value = 0;
};

const tabListElement = ref(null);

const onTabListResize = () => {
  tabListElement.value?.updateInkBar?.();
};

watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});

onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});

const lastPage = computed(() => Math.ceil(totalRecords.value / perPage.value));

const getPageQuery = (page) => {
  const query = { ...route.query };
  if (page > 1) query.page = page.toString();
  else delete query.page;
  return query;
};

const visiblePages = computed(() => {
  const current = currentPage.value;
  const last = lastPage.value;
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= last; i++) {
    if (
      i === 1 ||
      i === last ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
});

watch(
  () => route.query.page,
  (newPage) => {
    const p = parseInt(newPage) || 1;
    first.value = (p - 1) * perPage.value;
  },
);
</script>

<template>
  <Tabs v-model:value="activeTab" class="purchases__tabs" :scrollable="true">
    <TabList
      ref="tabListElement"
      :class="`purchases__tabs-headers tabs-bg-${backgroundColor}`"
      data-aos="fade-up"
      data-aos-easing="ease-out-cubic"
    >
      <Tab
        v-for="tab in computedTabs"
        :key="tab.id"
        :value="tab.id"
        class="purchases__tabs-title"
        @click="changeTab(tab.id)"
      >
        {{ tab.title }}
      </Tab>
    </TabList>
    <TabPanels class="purchases__tabs-content">
      <TabPanel v-for="tab in computedTabs" :key="tab.id" :value="tab.id">
        <div
          v-if="pending"
          class="purchases__overlay"
          :class="`purchases__overlay--${backgroundColor || 'white'}`"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <div class="purchases__overlay-spinner"></div>
        </div>
        <div class="purchases__items">
          <template v-if="results && results.length > 0">
            <template
              v-for="(purchase, purchaseIndex) in results"
              :key="purchaseIndex"
            >
              <PurchaseCard
                :id="purchase.id"
                :title="purchase.title"
                :date="purchase.date"
                :url="purchase.url"
                :theme="backgroundColor"
                data-aos="fade-up"
                :data-aos-delay="
                  purchaseIndex >= 1 ? 100 + 100 * purchaseIndex : 100
                "
              />
            </template>
          </template>
          <template v-else>
            {{ $t('purchases.empty') }}
          </template>
        </div>
        <nav
          v-if="lastPage > 1"
          class="purchases__pagination"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <NuxtLink
            :to="{ query: getPageQuery(currentPage - 1) }"
            class="purchases__pagination-btn purchases__pagination-btn--prev"
            :class="{
              'purchases__pagination-btn--disabled': currentPage === 1,
            }"
            rel="prev"
          >
            <CIcon
              class="purchases__pagination-btn-icon"
              name="direction-black"
              width="16"
              height="16"
            />
          </NuxtLink>

          <div class="purchases__pagination-items">
            <template v-for="(page, idx) in visiblePages" :key="idx">
              <span
                v-if="page === '...'"
                class="purchases__pagination-item purchases__pagination-item--dots"
              >
                ...
              </span>
              <NuxtLink
                v-else
                :to="{ query: getPageQuery(page) }"
                :class="[
                  'purchases__pagination-item',
                  {
                    'purchases__pagination-item--active': page === currentPage,
                  },
                ]"
              >
                {{ page }}
              </NuxtLink>
            </template>
          </div>

          <NuxtLink
            :to="{ query: getPageQuery(currentPage + 1) }"
            class="purchases__pagination-btn purchases__pagination-btn--next"
            :class="{
              'purchases__pagination-btn--disabled': currentPage === lastPage,
            }"
            rel="next"
          >
            <CIcon
              class="purchases__pagination-btn-icon"
              name="direction-black"
              width="16"
              height="16"
            />
          </NuxtLink>
        </nav>
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<style scoped lang="scss">
@keyframes loader-spin {
  to {
    transform: rotate(360deg);
  }
}
.purchases {
  $parent: &;
  &__overlay {
    inset: 0;
    pointer-events: all;
    position: absolute;
    z-index: 5;
    &--white {
      background: rgba($white, 0.65);
    }
    &--gray {
      background: rgba($gray, 0.65);
    }
    &--black {
      background: rgba($black, 0.65);
    }
    &-spinner {
      animation: loader-spin 0.8s linear infinite;
      border: 3px solid $divider;
      border-radius: 50%;
      border-top-color: $black;
      height: 40px;
      margin: 0 auto;
      position: sticky;
      top: calc(50vh - 20px);
      width: 40px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 3);
        @include responsive-spacing(height, 40);
        @include responsive-spacing(width, 40);
        top: calc(50vh - px-to-vw(20));
      }
    }
  }
  &__items {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  &__tabs {
    flex: 1 1 auto;
    min-width: 0;
    @include media-breakpoint-up(sm) {
      display: flex;
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-content) {
        border-bottom: 1px solid $divider;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-width, 1);
        }
      }
      &:deep(.p-tablist-tab-list) {
        justify-content: center;
        gap: 8px;
        @include media-breakpoint-up(md) {
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }

      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      padding: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding, 16);
      }
    }
    &-content {
      padding-top: 32px;
      @include media-breakpoint-up(sm) {
        padding-top: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 48);
      }
      &:deep(.p-tabpanel) {
        display: flex;
        flex-direction: column;
        gap: 32px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 32);
        }
      }
    }
  }
  &__pagination {
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 25px 16px 24px;
    @include media-breakpoint-up(sm) {
      gap: 28px;
      padding: 41px 48px 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 28);
      @include responsive-spacing(padding-top, 41);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-right, 48);
      @include responsive-spacing(padding-bottom, 40);
    }
    &-items {
      align-items: center;
      display: flex;
      gap: 4px;
      list-style: none;
      margin: 0;
      padding: 0;
      @include media-breakpoint-up(sm) {
        gap: 12px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
      }
    }
    &-btn {
      align-items: center;
      display: flex;
      justify-content: center;
      height: 32px;
      width: 32px;
      @include media-breakpoint-up(sm) {
        border: 1px solid $outline !important;
        border-radius: 50%;
        height: 48px;
        width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(width, 48);
      }
      &--disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      &--prev {
        #{$parent} {
          &__pagination-btn {
            &-icon {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    &-item {
      margin: 0;
      padding: 0;
      @include link-normal;
      align-items: center;
      border-radius: 50%;
      display: inline-flex;
      justify-content: center;
      height: 32px;
      min-width: 24px;
      text-decoration: none;
      @include media-breakpoint-up(sm) {
        border: 1px solid $divider;
        height: 48px;
        min-width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(min-width, 48);
      }
      &--active {
        background-color: $black;
        border-radius: 50%;
        color: $white;
        min-width: 32px;
        @include media-breakpoint-up(sm) {
          border: 1px solid $black;
          min-width: 48px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-width, 1);
          @include responsive-spacing(min-width, 48);
        }
      }
      &--dots {
        @include link-normal;
        display: inline-flex;
        min-width: 24px;
        justify-content: center;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(min-width, 24);
        }
      }
    }
  }
}
</style>
